import { useEffect, useMemo, useState } from "react";
import { Accordion, Alert, Badge, Button, Form, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ResourceComplianceHelpContent,
  ResourceComplianceHelpItem,
} from "@/types/resourceComplianceHelp";
import {
  getResourceComplianceHelpContent,
  markResourceComplianceQuestionHelpful,
  submitResourceComplianceHelpQuestion,
} from "@/services/resourceComplianceHelpService";
import { useRoleAccess } from "@/contexts/RoleAccessContext";

interface ResourceComplianceHelpModalProps {
  show: boolean;
  onHide: () => void;
}

const ResourceComplianceHelpModal: React.FC<ResourceComplianceHelpModalProps> = ({
  show,
  onHide,
}) => {
  const navigate = useNavigate();
  const { canAccessSolution } = useRoleAccess();
  type AccordionMode = "single" | "all";
  const canManageQuestions = canAccessSolution("forms");

  const [activeTab, setActiveTab] = useState<string>("faqs");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [faqAccordionMode, setFaqAccordionMode] = useState<AccordionMode>("single");
  const [communityAccordionMode, setCommunityAccordionMode] = useState<AccordionMode>("single");
  const [openFaqKey, setOpenFaqKey] = useState<string | null>(null);
  const [openCommunityKey, setOpenCommunityKey] = useState<string | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [questionCategory, setQuestionCategory] = useState("General");
  const [displayAsAnonymous, setDisplayAsAnonymous] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [helpfulUpdatingId, setHelpfulUpdatingId] = useState<number | null>(null);
  const [helpfulError, setHelpfulError] = useState<string | null>(null);
  const [content, setContent] = useState<ResourceComplianceHelpContent>({
    faqs: [],
    communityQuestions: [],
  });

  useEffect(() => {
    if (!show) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getResourceComplianceHelpContent();
        setContent(data);
      } catch (err: any) {
        setError(err?.message || "Failed to load help content.");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [show]);

  useEffect(() => {
    if (!show) {
      setSearchText("");
      setActiveTab("faqs");
      setFaqAccordionMode("single");
      setCommunityAccordionMode("single");
      setOpenFaqKey(null);
      setOpenCommunityKey(null);
      setQuestionText("");
      setQuestionCategory("General");
      setDisplayAsAnonymous(true);
      setSubmitting(false);
      setSubmitError(null);
      setSubmitSuccess(null);
      setHelpfulUpdatingId(null);
      setHelpfulError(null);
    }
  }, [show]);

  const normalizedSearch = searchText.trim().toLowerCase();

  const filterItems = (items: ResourceComplianceHelpItem[]) => {
    if (!normalizedSearch) return items;

    return items.filter((item) => {
      const haystack = `${item.questionText} ${item.answerText} ${item.category}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  };

  const filteredFaqs = useMemo(() => filterItems(content.faqs), [content.faqs, normalizedSearch]);
  const filteredCommunity = useMemo(
    () => filterItems(content.communityQuestions),
    [content.communityQuestions, normalizedSearch]
  );

  useEffect(() => {
    if (faqAccordionMode === "all") return;
    if (filteredFaqs.length === 0) {
      setOpenFaqKey(null);
      return;
    }

    setOpenFaqKey((prev) => {
      if (prev && filteredFaqs.some((item) => String(item.id) === prev)) {
        return prev;
      }
      return String(filteredFaqs[0].id);
    });
  }, [filteredFaqs, faqAccordionMode]);

  useEffect(() => {
    if (communityAccordionMode === "all") return;
    if (filteredCommunity.length === 0) {
      setOpenCommunityKey(null);
      return;
    }

    setOpenCommunityKey((prev) => {
      if (prev && filteredCommunity.some((item) => String(item.id) === prev)) {
        return prev;
      }
      return String(filteredCommunity[0].id);
    });
  }, [filteredCommunity, communityAccordionMode]);

  const getCategoryBadgeStyle = (category: string): { bg: string; textClass?: string } => {
    switch (category.toLowerCase()) {
      case "ownership":
        return { bg: "primary" };
      case "business unit":
        return { bg: "info" };
      case "cost center":
        return { bg: "dark" };
      case "compliance score":
        return { bg: "success" };
      case "reviews":
        return { bg: "warning", textClass: "text-dark" };
      case "storage":
        return { bg: "secondary" };
      case "dashboard":
        return { bg: "primary" };
      case "general":
      default:
        return { bg: "secondary" };
    }
  };

  const handleSubmitQuestion = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedQuestion = questionText.trim();
    if (!trimmedQuestion) {
      setSubmitError("Enter a question before submitting.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await submitResourceComplianceHelpQuestion({
        questionText: trimmedQuestion,
        category: questionCategory,
        displayAsAnonymous,
      });

      setQuestionText("");
      setQuestionCategory("General");
      setDisplayAsAnonymous(true);
      setSubmitSuccess("Your question was submitted. We will review it and post an answer if it is made public.");
      setActiveTab("community");
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || "Failed to submit question.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (questionId: number) => {
    setHelpfulUpdatingId(questionId);
    setHelpfulError(null);

    try {
      const result = await markResourceComplianceQuestionHelpful(questionId);
      setContent((prev) => ({
        ...prev,
        communityQuestions: prev.communityQuestions.map((q) =>
          q.id === questionId
            ? {
                ...q,
                helpfulCount: result.helpfulCount,
                hasUserVotedHelpful: result.hasUserVotedHelpful,
              }
            : q
        ),
      }));
    } catch (err: any) {
      setHelpfulError(err?.response?.data?.error || err?.message || "Failed to record helpful vote.");
    } finally {
      setHelpfulUpdatingId(null);
    }
  };

  const renderQuestionList = (
    items: ResourceComplianceHelpItem[],
    emptyMessage: string,
    source: "faq" | "community",
    accordionMode: AccordionMode,
    openKey: string | null,
    setAccordionMode: (mode: AccordionMode) => void,
    setOpenKey: (key: string | null) => void
  ) => {
    if (loading) {
      return (
        <div className="py-4 text-center text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading help content...
        </div>
      );
    }

    if (items.length === 0) {
      return <Alert variant="light" className="mb-0">{emptyMessage}</Alert>;
    }

    const itemKeys = items.map((item) => String(item.id));
    const isAllExpanded = accordionMode === "all";

    return (
      <>
        <div className="d-flex justify-content-end gap-2 mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setAccordionMode("all");
              if (!openKey && itemKeys.length > 0) {
                setOpenKey(itemKeys[0]);
              }
            }}
          >
            Expand all
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setAccordionMode("single");
              setOpenKey(null);
            }}
          >
            Collapse all
          </button>
        </div>

        <Accordion
          alwaysOpen={isAllExpanded}
          activeKey={isAllExpanded ? itemKeys : openKey}
          onSelect={(eventKey) => {
            if (isAllExpanded) return;
            if (typeof eventKey === "string") {
              setOpenKey(eventKey);
              return;
            }
            setOpenKey(null);
          }}
        >
        {items.map((item) => (
          <Accordion.Item eventKey={String(item.id)} key={item.id}>
            <Accordion.Header>
              <div className="d-flex justify-content-between align-items-center w-100 gap-2 pe-2">
                <div className="d-flex flex-column">
                  <span className="fw-semibold">{item.questionText}</span>
                </div>
                <div className="d-flex flex-column align-items-end">
                  <Badge bg={getCategoryBadgeStyle(item.category).bg} className={getCategoryBadgeStyle(item.category).textClass}>
                    {item.category}
                  </Badge>
                  {source === "community" && item.createdDt && (
                    <span className="small text-muted mt-1">
                      Asked {new Date(item.createdDt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <div className="text-muted">{item.answerText}</div>
              {source === "community" && (
                <div className="small text-muted mt-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex flex-wrap gap-3">
                    {item.displayName && <span>Asked by: {item.displayName}</span>}
                    {typeof item.helpfulCount === "number" && <span>Helpful votes: {item.helpfulCount}</span>}
                    {typeof item.isPublic === "boolean" && <span>{item.isPublic ? "Public" : "Private"}</span>}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={helpfulUpdatingId === item.id || item.hasUserVotedHelpful === true}
                    onClick={() => void handleMarkHelpful(item.id)}
                  >
                    {helpfulUpdatingId === item.id ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Saving...
                      </>
                    ) : item.hasUserVotedHelpful ? (
                      "Voted"
                    ) : (
                      "Helpful"
                    )}
                  </Button>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
        </Accordion>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Resource Health Help</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4 p-3 rounded-3 border bg-light">
          <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-end">
            <div className="flex-grow-1">
              <h5 className="mb-1">Need something explained?</h5>
              <div className="text-muted">
                Submit a question here and we will route it into the Resource Health help queue.
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => setActiveTab("ask")}>Ask a Question</Button>
              {canManageQuestions && (
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    onHide();
                    navigate("/admin/resource-compliance-questions");
                  }}
                >
                  Manage Questions
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mb-3">
          <Form.Control
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search FAQs and community questions"
            aria-label="Search help content"
          />
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
        {helpfulError && <Alert variant="danger">{helpfulError}</Alert>}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "faqs")}>
          <Tab eventKey="faqs" title={`FAQs (${filteredFaqs.length})`}>
            <div className="pt-3">
              {renderQuestionList(
                filteredFaqs,
                "No FAQs matched your search.",
                "faq",
                faqAccordionMode,
                openFaqKey,
                setFaqAccordionMode,
                setOpenFaqKey
              )}
            </div>
          </Tab>
          <Tab eventKey="community" title={`Community Questions (${filteredCommunity.length})`}>
            <div className="pt-3">
              {renderQuestionList(
                filteredCommunity,
                "No published community questions yet. Check back soon.",
                "community",
                communityAccordionMode,
                openCommunityKey,
                setCommunityAccordionMode,
                setOpenCommunityKey
              )}
            </div>
          </Tab>
          <Tab eventKey="ask" title="Ask a Question">
            <div className="pt-3">
              {submitError && <Alert variant="danger">{submitError}</Alert>}
              <Form onSubmit={handleSubmitQuestion} className="d-grid gap-3">
                <Form.Group>
                  <Form.Label>Question</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Describe what you need help with"
                    maxLength={2000}
                  />
                </Form.Group>

                <div className="d-flex flex-column flex-md-row gap-3">
                  <Form.Group className="flex-grow-1">
                    <Form.Label>Category</Form.Label>
                    <Form.Select value={questionCategory} onChange={(e) => setQuestionCategory(e.target.value)}>
                      <option value="General">General</option>
                      <option value="Ownership">Ownership</option>
                      <option value="Business Unit">Business Unit</option>
                      <option value="Cost Center">Cost Center</option>
                      <option value="Compliance Score">Compliance Score</option>
                      <option value="Reviews">Reviews</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id="resource-compliance-anonymous"
                      label="Submit anonymously"
                      checked={displayAsAnonymous}
                      onChange={(e) => setDisplayAsAnonymous(e.target.checked)}
                    />
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-end">
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      "Submit question"
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default ResourceComplianceHelpModal;
